import React, { useEffect, useCallback, useMemo } from "react";
import "./assets/scss/themes.scss";
import "./App.css"; // Use optimized CSS
import { useLocation, useNavigate } from "react-router-dom";
import Route from "./Routes";
import ToastComponent from "./common/ToastComponent";
import { publicRoutes } from "./Routes/allRoutes";
import SocketProvider from "./context/SocketProvider";
import ChatSocketBridge from "./context/ChatSocketBridge";

// Memoize the logout function
const useLogoutIfTokenExpire = () => {
  return useCallback(() => {
    const expirationTime = localStorage.getItem("expiryAt");
    if (expirationTime) {
      const currentTime = Date.now();
      if (currentTime > parseInt(expirationTime, 10)) {
        localStorage.removeItem("authUser");
        localStorage.removeItem("expiryAt");
        localStorage.removeItem("expiryIn");
      }
    }
  }, []);
};

// Memoize public routes check
const usePublicRoutes = () => {
  return useMemo(() => {
    return publicRoutes.map((route) => route.path).filter(path => path !== "*");
  }, []);
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const logoutIfTokenExpire = useLogoutIfTokenExpire();
  const publicRoutePaths = usePublicRoutes();

  // Combined useEffect for auth checks
  useEffect(() => {
    // Check token expiration
    logoutIfTokenExpire();

    // Authentication check
    const currentPath = location.pathname;
    const isPublicRoute = publicRoutePaths.includes(currentPath);

    if (!isPublicRoute) {
      const authUser = localStorage.getItem("authUser");
      let hasValidToken = false;

      if (authUser) {
        try {
          const authData = JSON.parse(authUser);
          hasValidToken = !!(authData?.token?.trim());
        } catch (error) {
          // Clear invalid data
          localStorage.removeItem("authUser");
          localStorage.removeItem("expiryAt");
          localStorage.removeItem("expiryIn");
        }
      }

      if (!hasValidToken) {
        navigate("/login", { replace: true });
      }
    }
  }, [location, navigate, logoutIfTokenExpire, publicRoutePaths]);

  return (
    <SocketProvider>
      <ChatSocketBridge />
      <Route />
      <ToastComponent />
    </SocketProvider>
  );
}

export default React.memo(App);